import { camelCase, toPairs } from 'lodash';
import moment from 'moment';
import { Squad } from 'api/models';
import {
  AssigneeValue,
  TeamId,
  UserId,
} from 'components/organisms/tasks/fields';
import { Task as NormalizedTask } from 'data/tasks/schema';
import { TaskField } from 'stores/fields';
import { Task as StoreTask, Task } from 'stores/tasks';

export type TaskLookupItem = Pick<Task, 'taskName' | 'taskId' | 'parentTask'>;

export const TaskLookupItem = ({
  taskName,
  taskId,
  parentTask,
}: TaskLookupItem): TaskLookupItem => ({
  taskName,
  taskId,
  parentTask,
});

const getTaskTree = (
  lookup: Record<number, TaskLookupItem>,
  task: TaskLookupItem,
): TaskLookupItem[] => {
  return task.parentTask == null || lookup[task.parentTask] == null
    ? [TaskLookupItem(task)]
    : getTaskTree(lookup, TaskLookupItem(lookup[task.parentTask])).concat(
        TaskLookupItem(task),
      );
};

export const toAssigneeValues = (
  squad: Squad = { users: [], teams: [] },
): AssigneeValue[] =>
  squad.users
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    .map(({ userId }) => UserId(userId) as AssigneeValue)
    .concat(squad.teams.map(({ teamId }) => TeamId(teamId)));

const ASSIGNEE_RL_META = 'assignee__rl_meta';

export const toStoreTask =
  (
    field_lookup: Record<number, TaskField>,
    tasks_lookup: Record<number, TaskLookupItem>,
  ) =>
  (normalized_task: NormalizedTask): StoreTask => {
    const {
      taskId,
      parentTask,
      project,
      responsible,
      fields: task_fields,
      subTaskOrder,
      linkedResources,
      ...task
    } = normalized_task;

    const fields: Record<string, any> = {};

    toPairs(field_lookup).forEach(([fieldId, field]) => {
      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      if (!field?.customField) {
        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        fields[field?.fieldName] =
          // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
          task[field?.fieldName] ?? (field?.metaData as any)?.default;
      } else {
        const f = task_fields.find((x) => x.fieldId.toString() === fieldId);

        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        fields[field.fieldName] =
          // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
          f?.fieldValue ?? (field.metaData as any)?.default;

        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        fields[`${field.fieldName}__rl_meta`] = f?.metaFieldValue;
      }
    });

    // These keys will not be stored ["commentThreadId", "projectPhase", "assigneeAuditMeta", "attachments", "subTasks", "parentTaskObject", "approvals"]
    return {
      ...fields,
      taskId,
      status: fields?.Status ?? fields?.status ?? task.status,
      private: task.private,
      responsible: (responsible ?? [])
        .filter(Boolean)
        .map(({ companyId }) => companyId),
      parentTask,
      project,
      taskTree: getTaskTree(tasks_lookup, normalized_task as any),
      phaseName: task.projectPhase?.projectPhaseName,
      projectPhaseId: task.projectPhase?.projectPhaseName,
      phaseId: task.projectPhase?.projectPhaseId,
      assignee: toAssigneeValues(task.assignee),
      [ASSIGNEE_RL_META]: task?.assignee,
      subTaskOrder,
      jiraIntegration: linkedResources?.find(
        (lr) => lr.externalIntegrationType === 'jira',
      )?.context?.jiraIssueUrl,
      completedLateBy:
        task.completedAt &&
        task.dueDate &&
        moment(task.completedAt).diff(moment(task.dueDate), 'days'),
      formInstanceId: task.formInstanceId,
      dependentTasks: task.dependentTasks,
      fieldsAccessMetaInBase3: task?.fieldsAccessMetaInBase3,
      sprint: task?.currentSprint?.sprintName,
      epic: task?.epic?.epicName,
      taskEffortSetting: task?.taskEffortSetting,
      taskEffortMeta: task?.taskEffortMeta,
      type: task?.type,
    } as unknown as StoreTask;
  };

export const toStoreTaskKanban =
  (
    field_lookup: Record<number, TaskField>,
    tasks_lookup: Record<number, TaskLookupItem>,
  ) =>
  (normalized_task: NormalizedTask): StoreTask => {
    const {
      taskId,
      parentTask,
      project,
      responsible,
      fields: task_fields,
      subTaskOrder,
      linkedResources,
      ...task
    } = normalized_task;

    const fields: Record<string, any> = {};

    task_fields?.forEach((f) => {
      const field = f as unknown as TaskField;

      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      if (!field?.customField) {
        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        fields[camelCase(field?.fieldName)] =
          // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
          task[field?.fieldName] ?? (field?.metaData as any)?.default;
      } else {
        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        fields[camelCase(field.fieldName)] =
          // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
          f?.fieldValue ?? (field.metaData as any)?.default;

        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        fields[`${camelCase(field.fieldName)}__rl_meta`] =
          // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
          f?.metaFieldValue ?? field?.fieldDataType === 'USER'
            ? f?.fieldValueUser
            : null;
      }
    });

    // These keys will not be stored ["commentThreadId", "projectPhase", "assigneeAuditMeta", "attachments", "subTasks", "parentTaskObject", "approvals"]
    return {
      ...fields,
      ...task,
      taskId,
      status: fields?.Status ?? fields?.status ?? task.status,
      private: task.private,
      responsible: (responsible ?? [])
        .filter(Boolean)
        .map(({ companyId }) => companyId),
      parentTask,
      project,
      taskTree: getTaskTree(tasks_lookup, normalized_task as any),
      phaseName: task.projectPhase?.projectPhaseName,
      projectPhaseId: task.projectPhase?.projectPhaseName,
      phaseId: task.projectPhase?.projectPhaseId,

      subTaskOrder,
      jiraIntegration: linkedResources?.find(
        (lr) => lr.externalIntegrationType === 'jira',
      )?.context?.jiraIssueUrl,
      completedLateBy:
        task.completedAt &&
        task.dueDate &&
        moment(task.completedAt).diff(moment(task.dueDate), 'days'),
      formInstanceId: task.formInstanceId,
      dependentTasks: task.dependentTasks,
      fieldsAccessMetaInBase3: task?.fieldsAccessMetaInBase3,
      sprint: task?.currentSprint?.sprintName,
      taskEffortSetting: task?.taskEffortSetting,
      taskEffortMeta: task?.taskEffortMeta,
      type: task?.type,
    } as unknown as StoreTask;
  };

export const toStoreTaskV2 =
  (tasks_lookup: Record<number, TaskLookupItem>) =>
  (normalized_task: NormalizedTask): StoreTask => {
    const {
      taskId,
      parentTask,
      project,
      responsible,
      fields: task_fields,
      subTaskOrder,
      linkedResources,
      ...task
    } = normalized_task;

    const fields: Record<string, any> = {};

    task_fields?.forEach((f) => {
      const field = f as unknown as TaskField;

      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      if (!field?.customField) {
        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        fields[camelCase(field?.fieldName)] =
          // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
          task[field?.fieldName] ?? (field?.metaData as any)?.default;
      } else {
        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        fields[camelCase(field.fieldName)] =
          // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
          f?.fieldValue ?? (field.metaData as any)?.default;

        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        fields[`${camelCase(field.fieldName)}__rl_meta`] = f?.metaFieldValue;
      }
    });

    return {
      ...task,
      ...fields,
      taskId,
      status: fields?.Status ?? fields?.status ?? task.status,
      private: task.private,
      responsible: (responsible ?? [])
        .filter(Boolean)
        .map(({ companyId }) => companyId),
      parentTask,
      project,
      taskTree: getTaskTree(tasks_lookup, normalized_task as any),
      phaseName: task.projectPhase?.projectPhaseName,
      projectPhaseId: task.projectPhase?.projectPhaseName,
      phaseId: task.projectPhase?.projectPhaseId,
      assignee: toAssigneeValues(task.assignee),
      [ASSIGNEE_RL_META]: task?.assignee,
      subTaskOrder,
      jiraIntegration: linkedResources?.find(
        (lr) => lr.externalIntegrationType === 'jira',
      )?.context?.jiraIssueUrl,
      completedLateBy:
        task.completedAt &&
        task.dueDate &&
        moment(task.completedAt).diff(moment(task.dueDate), 'days'),
      formInstanceId: task.formInstanceId,
      dependentTasks: task.dependentTasks,
      fieldsAccessMetaInBase3: task?.fieldsAccessMetaInBase3,
      sprint: task?.currentSprint?.sprintName,
      epic: task?.epic?.epicName,
      taskEffortSetting: task?.taskEffortSetting,
      taskEffortMeta: task?.taskEffortMeta,
      type: task?.type,
    } as unknown as StoreTask;
  };
